import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRowByLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartItemRowByLine(line);
    const rowsToRemove = cartItemRowToRemove ? this.#getRowsToRemove(cartItemRowToRemove) : [];
    const zeptoStorageIdentifiers = this.#getZeptoStorageIdentifiers(rowsToRemove);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      zeptoStorageIdentifiers,
    });

    if (!cartItemRowToRemove) return;

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Gets a cart item row by Shopify's cart line number.
   * @param {number} line - The line item index.
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRowByLine(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.line === line.toString());
  }

  /**
   * Gets a cart quantity selector by Shopify's cart line number.
   * @param {number} line - The line item index.
   * @returns {Element | undefined}
   */
  #getQuantitySelectorByLine(line) {
    const quantityInput = this.querySelector(`input[data-cart-line="${line}"]`);
    const quantitySelector = quantityInput?.closest('cart-quantity-selector-component');

    return quantitySelector || this.refs.quantitySelectors[line - 1];
  }

  /**
   * Gets every row that should disappear with the selected cart row.
   * @param {HTMLTableRowElement} rowToRemove - The row being removed.
   * @returns {HTMLTableRowElement[]}
   */
  #getRowsToRemove(rowToRemove) {
    const rowsToRemove = new Set([
      rowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === rowToRemove.dataset.key),
    ]);

    const engravingChargeKey = rowToRemove.dataset.engravingChargeKey;
    if (engravingChargeKey) {
      const engravingChargeRow = this.refs.cartItemRows.find((row) => row.dataset.key === engravingChargeKey);
      if (engravingChargeRow) rowsToRemove.add(engravingChargeRow);
    }

    for (const row of this.refs.cartItemRows) {
      if (row.dataset.engravingParentKey === rowToRemove.dataset.key) {
        rowsToRemove.add(row);
      }
    }

    const nextRow = rowToRemove.nextElementSibling;
    if (
      nextRow instanceof HTMLTableRowElement &&
      nextRow.classList.contains('cart-items__table-row--engraving-charge')
    ) {
      const nextParentKey = nextRow.dataset.engravingParentKey;
      const belongsToOtherLiveParent =
        Boolean(nextParentKey) &&
        nextParentKey !== rowToRemove.dataset.key &&
        this.#hasLiveProductRowKey(nextParentKey);

      if (!belongsToOtherLiveParent) {
        rowsToRemove.add(nextRow);
      }
    }

    const engravingChargeLine = parseInt(rowToRemove.dataset.engravingChargeLine || '', 10);
    if (!Number.isNaN(engravingChargeLine)) {
      const engravingChargeRow = this.#getCartItemRowByLine(engravingChargeLine);
      if (engravingChargeRow) rowsToRemove.add(engravingChargeRow);
    }

    return Array.from(rowsToRemove);
  }

  /**
   * Whether a product row with this cart line key is currently rendered.
   * Charge rows are ignored so a stale parent key on a charge is not treated as live.
   * @param {string | undefined} itemKey
   * @returns {boolean}
   */
  #hasLiveProductRowKey(itemKey) {
    if (!itemKey) return false;

    return this.refs.cartItemRows.some(
      (row) => row.dataset.key === itemKey && row.dataset.engravingLineRole !== 'charge'
    );
  }

  /**
   * Builds a key-based cart update for engraved product + charge pairs.
   * @param {HTMLTableRowElement | undefined} row - The row being changed.
   * @param {number} quantity - The target quantity.
   * @returns {{ updates: Record<string, number> } | null}
   */
  #getPairedCartUpdate(row, quantity) {
    if (!row) return null;

    const itemKey = row.dataset.key;
    if (!itemKey) return null;

    let engravingChargeKey = row.dataset.engravingChargeKey;

    if (!engravingChargeKey) {
      const nextRow = row.nextElementSibling;
      if (
        nextRow instanceof HTMLTableRowElement &&
        nextRow.classList.contains('cart-items__table-row--engraving-charge') &&
        nextRow.dataset.key
      ) {
        const nextParentKey = nextRow.dataset.engravingParentKey;
        const belongsToOtherLiveParent =
          Boolean(nextParentKey) &&
          nextParentKey !== itemKey &&
          this.#hasLiveProductRowKey(nextParentKey);

        if (!belongsToOtherLiveParent) {
          engravingChargeKey = nextRow.dataset.key;
        }
      }
    }

    if (!engravingChargeKey) return null;

    const chargeRow = this.refs.cartItemRows.find((cartRow) => cartRow.dataset.key === engravingChargeKey);
    const linkedParentKey = chargeRow?.dataset.engravingParentKey;

    if (linkedParentKey && linkedParentKey !== itemKey && this.#hasLiveProductRowKey(linkedParentKey)) {
      return null;
    }

    if (!linkedParentKey && chargeRow) {
      const productLine = Number.parseInt(row.dataset.line || '', 10);
      const chargeLine = Number.parseInt(chargeRow.dataset.line || '', 10);

      if (
        Number.isFinite(productLine) &&
        Number.isFinite(chargeLine) &&
        chargeLine === productLine - 1
      ) {
        const productBeforeCharge = this.refs.cartItemRows.find(
          (cartRow) => Number.parseInt(cartRow.dataset.line || '', 10) === chargeLine - 1
        );

        if (productBeforeCharge?.dataset.engravingChargeKey === engravingChargeKey) {
          return null;
        }
      }
    }

    return {
      updates: {
        [itemKey]: quantity,
        [engravingChargeKey]: quantity,
      },
    };
  }

  /**
   * Gets row identifiers that can safely scope best-effort Zepto storage cleanup.
   * @param {HTMLTableRowElement[]} rows - Removed rows.
   * @returns {string[]}
   */
  #getZeptoStorageIdentifiers(rows) {
    const identifiers = new Set();

    for (const row of rows) {
      [
        row.dataset.key,
        row.dataset.itemKey,
        row.dataset.productId,
        row.dataset.variantId,
        row.dataset.engravingChargeKey,
        row.dataset.engravingParentKey,
      ].forEach((identifier) => {
        if (identifier && identifier.length > 3) identifiers.add(identifier.toLowerCase());
      });
    }

    return Array.from(identifiers);
  }

  /**
   * Clears only Zepto-like storage entries that reference removed cart identifiers.
   * @param {string[]} identifiers - Removed line/product identifiers.
   */
  #clearZeptoCustomizationState(identifiers) {
    if (!identifiers.length) return;

    const zeptoTerms = ['zepto', 'pplr', 'personalizer', 'personalization', 'cstmfy'];

    for (const storage of [localStorage, sessionStorage]) {
      try {
        for (let index = storage.length - 1; index >= 0; index--) {
          const key = storage.key(index);
          if (!key) continue;

          const value = storage.getItem(key) || '';
          const haystack = `${key} ${value}`.toLowerCase();
          const isZeptoState = zeptoTerms.some((term) => haystack.includes(term));
          const referencesRemovedLine = identifiers.some((identifier) => haystack.includes(identifier));

          if (isZeptoState && referencesRemovedLine) {
            storage.removeItem(key);
          }
        }
      } catch (_) {
        // Storage may be unavailable in private browsing or blocked browser contexts.
      }
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string[]} [config.zeptoStorageIdentifiers] - Zepto storage cleanup identifiers.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const lineItemRow = this.#getCartItemRowByLine(line);
    const pairedCartUpdate = this.#getPairedCartUpdate(lineItemRow, quantity);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sectionsPayload = {
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };
    const body = JSON.stringify(
      pairedCartUpdate
        ? {
            ...pairedCartUpdate,
            ...sectionsPayload,
          }
        : {
            line: line,
            quantity: quantity,
            ...sectionsPayload,
          }
    );
    const cartUpdateUrl = pairedCartUpdate ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;

    cartTotal?.shimmer();

    fetch(`${cartUpdateUrl}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        if (quantity === 0 && config.zeptoStorageIdentifiers?.length) {
          this.#clearZeptoCustomizationState(config.zeptoStorageIdentifiers);
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: parsedResponseText.item_count ?? newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getQuantitySelectorByLine(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
